import { useEffect, useMemo, useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import UnitSelect from "@/components/unit-select";
import { Trash2Icon, Rows3 } from "lucide-react";
import { toast } from "sonner";
import { SectionHeader } from "./inspector-helpers";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { FormulaField } from "@/components/formula/FormulaField";
import { FormulaUnitWarning } from "@/components/formula/FormulaUnitWarning";
import type { FormulaContextInput } from "@/components/formula/context";
import { CodeGenerateButton } from "@/components/code-generate-button";
import { AppliesToScalesField } from "@/features/harvest-operation-template/applies-to-scales-field";
import { toCodeIdentifier } from "@/lib/utils";
import { useEditorConfig } from "./editor-config-context";
import { columnTextOptions } from "@brewbase/lib";

interface InspectorTabularColumnProps {
  templateAttributeId: number;
  columnSortOrder: number;
  isLocked: boolean;
  onClearSelection: () => void;
}

export function InspectorTabularColumn({
  templateAttributeId,
  columnSortOrder,
  isLocked,
  onClearSelection,
}: InspectorTabularColumnProps) {
  const { useTabularColumnsApi, tabularColumnFormulaContextKind } =
    useEditorConfig();
  const { columns, replaceColumns, setColumnsCache } =
    useTabularColumnsApi(templateAttributeId);

  // Per-column scale applicability is harvest-only (mirrors the operation
  // attribute "Applies to scales"). Filtered at display time, not instantiation.
  const isHarvestColumn =
    tabularColumnFormulaContextKind === "harvest-tabular-column";

  const column = useMemo(
    () => columns?.find((c) => c.sort_order === columnSortOrder) ?? null,
    [columns, columnSortOrder],
  );

  // A computed column without a formula is invalid server-side, so picking
  // "Computed" is held locally until a formula is entered, then saved together.
  const [pendingComputed, setPendingComputed] = useState(false);
  // Likewise "Dropdown": stored as a text column with options, so it is held
  // locally until the first option is entered (an empty list is plain text).
  const [pendingDropdown, setPendingDropdown] = useState(false);
  // The name as it is being typed, so the code wand works from what's on
  // screen rather than the last saved name.
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  useEffect(() => {
    setPendingComputed(false);
    setPendingDropdown(false);
    setNameDraft(null);
  }, [templateAttributeId, columnSortOrder]);

  // The newest column set written, ahead of the query: the cache write notifies
  // on a later tick, so an edit right after another (the wand after the name
  // saves on blur) would otherwise build on the set from before it. A refetch
  // (a new `columns`) takes over again.
  const latestColumns = useRef(columns);
  const renderedColumns = useRef(columns);
  if (renderedColumns.current !== columns) {
    renderedColumns.current = columns;
    latestColumns.current = columns;
  }

  if (!column) return null;

  const showComputed = column.is_computed || pendingComputed;
  // Dropdown is a type to the author, a text column with options underneath.
  const showDropdown =
    !showComputed && (columnTextOptions(column) !== null || pendingDropdown);

  // is_index is authoritative — the editor pins the index column first and
  // hides the "computed" type for it, but it can still be renamed and deleted.
  const isIndexColumn = column.is_index;
  // A table has at most one row axis. Offer promotion only when none exists and
  // this is a plain (non-computed) value column — any of date/number/text works.
  const tableHasAxis = columns?.some((c) => c.is_index) ?? false;
  const canPromoteToAxis =
    !isIndexColumn && !showComputed && !showDropdown && !tableHasAxis;

  // Every save sends the whole column set, so write it to the cache first: a
  // second edit before the first round-trip (e.g. the code wand right after
  // typing a name) then builds on the new name instead of putting back the old.
  const updateColumn = (updates: Record<string, unknown>) => {
    const base = latestColumns.current;
    if (!base) return;
    const next = base.map((c) =>
      c.sort_order === columnSortOrder ? { ...c, ...updates } : c,
    );
    latestColumns.current = next;
    setColumnsCache(next);
    replaceColumns(next);
  };

  const deleteColumn = () => {
    if (!columns) return;
    replaceColumns(
      columns
        .filter((c) => c.sort_order !== columnSortOrder)
        .map((c, i) => ({ ...c, sort_order: i })),
    );
    onClearSelection();
  };

  return (
    <div className="space-y-0">
      <SectionHeader title={isIndexColumn ? "Row Axis" : "Column Settings"} />
      <div className="px-3 py-3 space-y-3">
        <ColumnNameField
          // Remounts per column, so a just-added column's name field takes focus.
          key={`${templateAttributeId}:${columnSortOrder}`}
          value={column.name}
          disabled={isLocked}
          onDraftChange={setNameDraft}
          onCommit={(name) => updateColumn({ name })}
        />

        <ColumnCodeField
          value={column.code ?? ""}
          name={nameDraft ?? column.name}
          disabled={isLocked}
          onCommit={(code) => updateColumn({ code })}
        />

        <div>
          <Label className="text-xs font-medium">
            {isIndexColumn ? "Axis type" : "Type"}
          </Label>
          <Select
            value={
              showComputed
                ? "computed"
                : showDropdown
                  ? "dropdown"
                  : column.data_type
            }
            onValueChange={(value) => {
              if (value === "computed") {
                setPendingDropdown(false);
                setPendingComputed(true);
                return;
              }
              if (value === "dropdown") {
                setPendingComputed(false);
                setPendingDropdown(true);
                return;
              }
              setPendingComputed(false);
              setPendingDropdown(false);
              updateColumn({
                is_computed: false,
                data_type: value,
                formula: null,
                text_options: null,
              });
            }}
            disabled={isLocked}
          >
            <SelectTrigger className="h-8 text-sm mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="string">{isIndexColumn ? "Label" : "Text"}</SelectItem>
              {!isIndexColumn && (
                <SelectItem value="dropdown">Dropdown</SelectItem>
              )}
              <SelectItem value="datetime">{isIndexColumn ? "Time" : "Date/Time"}</SelectItem>
              {!isIndexColumn && (
                <SelectItem value="computed">Computed</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {canPromoteToAxis && (
          <div className="bg-warning/20 px-2 py-2 space-y-1.5">
            <p className="text-xs text-warning-foreground">
              This table has no row axis — its rows aren't indexed or ordered.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              disabled={isLocked}
              onClick={() => updateColumn({ is_index: true, required: true })}
            >
              <Rows3 className="h-3 w-3 mr-1" />
              Turn into row axis
            </Button>
          </div>
        )}

        {column.data_type === "number" && !showComputed && (
          <div>
            <Label className="text-xs font-medium">Unit</Label>
            <UnitSelect
              value={column.unit || ""}
              onChange={(value) => updateColumn({ unit: value || null })}
              placeholder="Select unit..."
              className="mt-1"
            />
          </div>
        )}

        {showDropdown && !isIndexColumn && (
          <ColumnOptionsField
            // Remount on an outside change so the draft never mirrors it by effect.
            key={JSON.stringify(columnTextOptions(column))}
            value={columnTextOptions(column)}
            disabled={isLocked}
            onCommit={(text_options) => {
              updateColumn({
                data_type: "string",
                is_computed: false,
                formula: null,
                text_options,
              });
              setPendingDropdown(false);
            }}
          />
        )}

        {showComputed && tabularColumnFormulaContextKind && (
          <div>
            <TabularFormulaField
              formula={column.formula || ""}
              templateAttributeId={templateAttributeId}
              contextKind={tabularColumnFormulaContextKind}
              isLocked={isLocked}
              onSave={(val) => {
                const formula = val.trim();
                if (!formula) {
                  toast.error("Computed columns require a formula");
                  return;
                }
                updateColumn({
                  is_computed: true,
                  data_type: "number",
                  formula,
                });
              }}
            />
            {!column.is_computed && (
              <p className="text-xs text-muted-foreground mt-1">
                Enter a formula to save this column as computed.
              </p>
            )}
          </div>
        )}

        {column.is_computed && column.data_type === "number" && (
          <div className="flex flex-wrap gap-4">
            <div className="min-w-0 flex-1">
              <Label className="text-xs font-medium">Result Unit</Label>
              <UnitSelect
                value={column.formula_result_unit || ""}
                onChange={(value) =>
                  updateColumn({ formula_result_unit: value || null })
                }
                placeholder="Unit..."
                className="mt-1 w-full max-w-none"
              />
            </div>
            <div className="min-w-0 flex-1">
              <Label className="text-xs font-medium">Display Unit</Label>
              <UnitSelect
                value={column.formula_result_display_unit || ""}
                onChange={(value) =>
                  updateColumn({
                    formula_result_display_unit: value || null,
                  })
                }
                placeholder="Display..."
                className="mt-1 w-full max-w-none"
              />
            </div>
          </div>
        )}

        {column.is_computed && column.data_type === "number" && (
          <FormulaUnitWarning
            formula={column.formula || ""}
            declaredUnit={column.formula_result_unit}
            context={
              {
                kind: tabularColumnFormulaContextKind,
                templateAttributeId,
              } as FormulaContextInput
            }
          />
        )}
        {isHarvestColumn && !isIndexColumn && (
          <div>
            <Label className="text-xs font-medium">Applies to scales</Label>
            <AppliesToScalesField
              value={column.applicable_scale_ids}
              onChange={(ids) => updateColumn({ applicable_scale_ids: ids })}
              disabled={isLocked}
              className="mt-1"
            />
          </div>
        )}
      </div>

      {/* Delete */}
      {!isLocked && (
        <div className="px-3 py-3 border-t border-border">
          <DeleteConfirmDialog
            title="Delete Column"
            description={`Are you sure you want to delete column "${column.name}"? This cannot be undone.`}
            onConfirm={deleteColumn}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 text-xs"
              >
                <Trash2Icon className="h-3 w-3 mr-1" />
                Delete column
              </Button>
            }
          />
        </div>
      )}
    </div>
  );
}

/** How long typing has to pause before the name saves on its own. */
const NAME_AUTOSAVE_MS = 600;
/** The name a column is added with; a new column opens with it selected. */
const NEW_COLUMN_NAME = "New Column";

function ColumnNameField({
  value,
  disabled,
  onDraftChange,
  onCommit,
}: {
  value: string;
  disabled?: boolean;
  onDraftChange?: (draft: string) => void;
  onCommit: (name: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const autosave = useRef<ReturnType<typeof setTimeout> | null>(null);

  const save = (text: string, revertIfBlank: boolean) => {
    if (autosave.current) clearTimeout(autosave.current);
    autosave.current = null;
    const next = text.trim();
    if (next && next !== value) onCommit(next);
    else if (revertIfBlank) setDraft(value);
  };

  return (
    <div>
      <Label className="text-xs font-medium">Name</Label>
      <Input
        value={draft}
        onChange={(e) => {
          const text = e.target.value;
          setDraft(text);
          onDraftChange?.(text);
          // Saves once typing pauses, not only on Enter or blur.
          if (autosave.current) clearTimeout(autosave.current);
          autosave.current = setTimeout(() => save(text, false), NAME_AUTOSAVE_MS);
        }}
        onBlur={() => save(draft, true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            if (autosave.current) clearTimeout(autosave.current);
            setDraft(value);
          }
        }}
        // A just-added column opens ready to be named.
        autoFocus={value === NEW_COLUMN_NAME}
        onFocus={(e) => {
          if (e.currentTarget.value === NEW_COLUMN_NAME) e.currentTarget.select();
        }}
        disabled={disabled}
        className="h-7 text-sm mt-1"
      />
    </div>
  );
}

/**
 * A dropdown column's values, one per line, saved on blur. `value` is null
 * while the column is only pending as a dropdown. A dropdown keeps at least one
 * option (free text is the Text type), and a duplicate is refused here rather
 * than by the server.
 */
function ColumnOptionsField({
  value,
  disabled,
  onCommit,
}: {
  value: string[] | null;
  disabled?: boolean;
  onCommit: (options: string[]) => void;
}) {
  const [draft, setDraft] = useState((value ?? []).join("\n"));

  const commit = () => {
    const lines = draft
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l !== "");
    const duplicate = lines.find((l, i) => lines.indexOf(l) !== i);
    if (duplicate) {
      toast.error(`"${duplicate}" is listed twice`);
      return;
    }
    if (lines.length === 0) {
      if (value) {
        toast.error(
          "A dropdown needs at least one option. Pick Text for free text.",
        );
      }
      setDraft((value ?? []).join("\n"));
      return;
    }
    if (JSON.stringify(lines) !== JSON.stringify(value)) onCommit(lines);
    setDraft(lines.join("\n"));
  };

  return (
    <div>
      <Label className="text-xs font-medium">Dropdown options</Label>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") setDraft((value ?? []).join("\n"));
        }}
        disabled={disabled}
        placeholder={"One per line, e.g.\nTC\nBC\nETB"}
        rows={Math.min(8, Math.max(3, (value?.length ?? 0) + 1))}
        className="text-sm mt-1"
      />
      <p className="text-xs text-muted-foreground mt-1">
        {value
          ? "Cells offer only these values."
          : "Enter at least one option to save the column as a dropdown."}
      </p>
    </div>
  );
}

function ColumnCodeField({
  value,
  name,
  disabled,
  onCommit,
}: {
  value: string;
  name: string;
  disabled?: boolean;
  onCommit: (code: string | null) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = (next: string) => {
    const normalized = toCodeIdentifier(next) || null;
    if (normalized !== (value || null)) onCommit(normalized);
  };

  return (
    <div>
      <Label className="text-xs font-medium">Code</Label>
      <div className="flex items-start gap-1 mt-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit(draft);
          }}
          disabled={disabled}
          placeholder="(optional) e.g. mass"
          className="h-7 text-sm font-mono flex-1"
        />
        <CodeGenerateButton
          sourceName={name}
          disabled={disabled}
          onGenerate={(code) => {
            setDraft(code);
            onCommit(code);
          }}
        />
      </div>
    </div>
  );
}

function TabularFormulaField({
  formula,
  templateAttributeId,
  contextKind,
  isLocked,
  onSave,
}: {
  formula: string;
  templateAttributeId: number;
  contextKind: "harvest-tabular-column" | "test-tabular-column";
  isLocked: boolean;
  onSave: (val: string) => void;
}) {
  const context = {
    kind: contextKind,
    templateAttributeId,
  } as FormulaContextInput;

  return (
    <div>
      <Label className="text-xs font-medium">Formula</Label>
      <div className="mt-1">
        <FormulaField
          value={formula}
          onSave={onSave}
          context={context}
          disabled={isLocked}
          placeholder="e.g. row.mass / row.volume"
          title="Column formula"
        />
      </div>
    </div>
  );
}
